import { parseSearchWith, stringifySearchWith } from '@tanstack/react-router';
import { Effect, Schema } from 'effect';

/**
 * An optional search param that degrades to absent instead of failing the
 * route.
 *
 * The router JSON-parses search values, so `?x=42` arrives as a number, `?x`
 * as `true`, and a duplicated `?x=a&x=b` as an array. Failing validation on
 * any of those would trade the whole page for an error component over a param
 * that nothing load-bearing reads.
 *
 * `catchDecoding` wraps the optional *field* rather than the schema inside it:
 * a type mismatch fails at the field, so a recovery placed further in is never
 * reached. Keeping it off the enclosing `Struct` matters just as much — there
 * a sibling field's failure would be swallowed along with this one.
 *
 * Recovery leaves the KEY present with an `undefined` value, which reads the
 * same as absent at every call site but is not the same object shape.
 */
export const optionalCatching = <S extends Schema.Top>(schema: S) =>
  Schema.optional(schema).pipe(
    Schema.catchDecoding(() => Effect.succeedSome(undefined)),
  );

/**
 * `optionalCatching` with a value to fall back to instead of absence — the
 * shape of a param that has a meaningful default, like a paginated route's
 * first page.
 *
 * Absence is still absence: `Schema.optional` succeeds on a missing key
 * without the recovery running, so the fallback applies only to a value that
 * was present and failed to decode.
 */
export const optionalCatchingTo = <S extends Schema.Top>(
  schema: S,
  fallback: NoInfer<S['Type']>,
) =>
  Schema.optional(schema).pipe(
    // The type argument is explicit because `catchDecoding` infers its own `S`
    // from the schema it wraps — the OPTIONAL one, whose `Type` is
    // `S['Type'] | undefined`. Left implicit, that inference collides with the
    // outer generic and TypeScript reports the `this` of the pipe as unassignable.
    Schema.catchDecoding<Schema.optional<S>>(() =>
      Effect.succeedSome(fallback),
    ),
  );

/**
 * The router's `parseSearch` value hook, shared with `stringifySearch` below.
 *
 * TanStack's default is a bare `JSON.parse`, which turns an 18-digit Meta id
 * into a rounded double and `662E41` into `6.62e43`; the server then
 * re-serialises the search, sees a different URL and 307s every cache miss
 * to the rounded one. Upstream: TanStack/router#7650 is the bug, #7652 is
 * this round-trip rule, #7860 the unsafe-integer variant shipped here first.
 * All three are open and unmerged as of router-core 1.171.15, so the fix
 * lives here.
 *
 * A throw means "leave it a string": `parseSearchWith` catches per value, and
 * `stringifySearchWith(JSON.stringify, parseSearchValue)` runs the same
 * predicate to decide whether a string must be JSON-quoted to parse back as
 * a string. That symmetry is why this is a plain function and not an Effect
 * Schema: it is a serialisation rule the router calls synchronously on every
 * value of every navigation, before any route matches, and both directions
 * must agree byte for byte. Validation stays in each route's
 * `validateSearch`, via `Schema.toStandardSchemaV1` and the helpers above.
 */
export function parseSearchValue(value: string): unknown {
  const parsed: unknown = JSON.parse(value);
  if (typeof parsed === 'number' && String(parsed) !== value) {
    throw new SyntaxError(
      `numeric search value does not survive a round-trip: ${value}`,
    );
  }
  return parsed;
}

export const parseSearch = parseSearchWith(parseSearchValue);

export const stringifySearch = stringifySearchWith(
  JSON.stringify,
  parseSearchValue,
);
